<template>
  <article class="container-fluid">
    <PageHeader
      :page="page"
      :actions="pageActionConfig"
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    />

    <add-assistant-card
      v-if="toggleAddAssistantCard"
      ref="addAssistantCardRef"
      :assistants="assistants"
      title="Añadir Asistente"
      @on-close="toggleAddAssistantCard = false"
      @on-save="onAddAssistant"
      @on-region-change="onRegionChangeHandler"
    />
    <add-assistant-section
      v-if="toggleAddAssistantSection"
      ref="addAssistantSectionRef"
      :assistants="assistants"
      :academic-year-id="academicYearId"
      :school-id="schoolId"
      :is-public-school="isPublicSchool"
      :is-ssn-required-for-assistant="isSSNRequiredForAssistant"
      title="Añadir Asistente"
      @on-close="toggleAddAssistantSection = false"
      @on-save="onAddManualAssistant"
    />

    <Board :details="schoolBoardDetails" />

    <template v-if="showNotResults">
      <CustomResultNotFound
        class="mt-4 row pt-3"
        text="No hay información disponible."
      />
    </template>

    <template v-else>
      <ServiceAssistantList
        class="mt-4"
        :collections="collections"
        :academic-year-id="academicYearId"
        @reload-list="onSearch"
      />

      <Pagination
        v-if="pagination.totalItemCount > pagination.perPage"
        :data="pagination"
        :page-size="pagination.perPage"
        @pagination-go-page="onSearch"
      />
    </template>
  </article>
</template>

<script>
import Board from 'Board';
import PageHeader from 'PageHeader';
import Pagination from 'pagination';
import CustomResultNotFound from 'custom-result-not-found';
import ServiceAssistantList from './components/ServiceAssistantList.vue';
import AddAssistantCard from './components/AddAssistantCard.vue';
import AddAssistantSection from './components/AddAssistantSection.vue';
import {
  getNecessitiesList,
  getServiceAssistantSchoolInformation,
  getServiceAssistantsList,
  assistantsAvailablesToAdd,
  addAssistantSchool,
  certifyAssistants,
  getAssistantSchoolReport,
  validateIsAnyAssistantCertified as _validateIsAnyAssistantCertified,
} from '@/modules/human-resources/api';
import resourcesService from '@/services/resources';
import locationService from '@/services/api/location.api';
import DocumentsService from '@/services/documents';
import mixAlert from '@/mixin/alert.mixin';

const initialBoardDetailsModel = (
  { academicYear, region, municipality, school } = {
    academicYear: null,
    region: null,
    municipality: null,
    school: null,
  }
) => ({
  academicYear,
  region,
  municipality,
  school,
});

export default {
  name: 'Manage',
  mixins: [mixAlert],
  components: {
    Board,
    PageHeader,
    Pagination,
    CustomResultNotFound,
    ServiceAssistantList,
    AddAssistantCard,
    AddAssistantSection,
  },
  props: {
    schoolId: { type: [Number, String], default: null },
    academicYearId: { type: [Number, String], default: 0 },
  },
  data() {
    return {
      page: {
        icon: 'fas fa-user-edit',
        title: 'Manejo de Asistente de Servicio',
        routes: [
          'Inicio',
          'Recursos Humanos',
          'Manejo de Asistente de Servicio',
        ],
      },
      collections: {
        necessities: [],
        serviceAssistants: [],
      },
      boardDetailsModel: initialBoardDetailsModel(),
      pagination: {
        perPage: 20,
        currentPage: 1,
        totalItemCount: 0,
        pageCount: 0,
        count: 0,
      },
      toggleAddAssistantCard: false,
      toggleAddAssistantSection: false,
      assistants: [],
      isAnyAssistantCertified: false,
      isPublicSchool: null,
    };
  },
  computed: {
    pageActionConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => this.$router.go(-1),
        },
        {
          text: 'Añadir',
          icon: 'fas fa-file-plus',
          variant: 'outline-success',
          visible:
            !this.toggleAddAssistantCard &&
            !this.toggleAddAssistantSection &&
            this.hasPermissions('rrhh.serviceassistant.action.add'),
          click: () => {
            this.toggleAddAssistantSection = false;
            this.toggleAddAssistantCard = true;
          },
        },
        {
          text: 'Añadir Asistente',
          icon: 'fas fa-user-plus',
          variant: 'outline-success',
          visible:
            !this.toggleAddAssistantCard &&
            !this.toggleAddAssistantSection &&
            this.hasPermissions(
              'academic.hr.assistantservices.canaddmanualassistant'
            ),
          click: () => {
            this.toggleAddAssistantCard = false;
            this.toggleAddAssistantSection = true;
          },
        },
        {
          text: this.assistantCertificationButtonText,
          visible: this.canShowCertify,
          icon: 'fas fa-file-certificate',
          variant: 'success',
          click: () => this.certificate(),
        },
        {
          text: 'Imprimir',
          visible: this.canShowCertify,
          icon: 'fas fa-print',
          variant: 'outline-success',
          click: () => this.printAssistantSchoolReport(),
        },
      ];
    },
    schoolBoardDetails() {
      const {
        academicYear,
        region,
        municipality,
        school,
      } = this.boardDetailsModel;

      return [
        [
          {
            label: 'Año Académico:',
            data: academicYear?.name,
            icon: 'fas fa-calendar-edit',
          },
          {
            label: 'Región:',
            data: region?.name,
            icon: 'fas fa-ballot-check',
          },
          {
            label: 'Municipio:',
            data: municipality?.name,
            icon: 'fas fa-ballot-check',
          },
          {
            label: 'Escuela:',
            data: school?.name,
            icon: 'fas fa-ballot-check',
          },
        ],
      ];
    },
    showNotResults() {
      return this.collections.serviceAssistants.length == 0;
    },
    canShowCertify() {
      return this.hasPermissions('rrhh.serviceassistant.action.certify');
    },
    getRegionOfUser() {
      const { region } = this.boardDetailsModel;

      if (this.userProfile.regionid) {
        const regionsId = Array.from(this.userProfile.regionid.split(','));
        return regionsId.find((c) => c == region.id);
      }

      return region?.id;
    },
    assistantCertificationButtonText() {
      return this.isAnyAssistantCertified
        ? 'Sincronizar'
        : 'Paso 1 - Certificar';
    },
    isSSNRequiredForAssistant() {
      return this.isPublicSchool;
    },
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'ServiceAssistant' });
    },
    hasPermissions(permission) {
      return this.$store.getters.HasPermission(permission);
    },
    async fetchResources() {
      const { data: necesitiesList } = await getNecessitiesList();
      this.collections.necessities = necesitiesList;

      this.collections.resources = await resourcesService.LoadValidationMessages(
        ['R01210005', 'R01210008', 'R01210009']
      );
    },
    async fetchServiceAssistantSchoolInfo(schoolId, academicYearId) {
      const data = await getServiceAssistantSchoolInformation(
        schoolId,
        academicYearId
      );

      this.boardDetailsModel = initialBoardDetailsModel(data);
    },
    async fetchIsPublicSchool() {
      if (!this.schoolId) return;
      try {
        const { data } = await locationService.isPublicSchool(this.schoolId);
        this.isPublicSchool = Boolean(data);
      } catch {
        this.isPublicSchool = null;
      }
    },
    async onSearch(page = 1) {
      const searchDTO = {
        schoolId: this.schoolId,
        academicYearId: this.academicYearId,
        pageIndex: page,
        pageSize: this.pagination.perPage,
      };

      const {
        data,
        currentPage,
        count,
        pageCount,
        totalItemCount,
      } = await getServiceAssistantsList(searchDTO);

      this.collections.serviceAssistants = [...data];
      this.pagination.totalItemCount = totalItemCount;
      this.pagination.count = count;
      this.pagination.currentPage = currentPage;
      this.pagination.pageCount = pageCount;
    },
    async onAddAssistant(model) {
      let payload = {
        assistantId: model.assistant.id,
        locationSchoolId: this.schoolId,
        academicYearId: this.academicYearId,
      };

      await addAssistantSchool(payload);

      this.ShowToast(
        '',
        'La información ha sido guardada exitosamente.',
        'success'
      );

      this.$refs.addAssistantCardRef.onClose();

      await this.onSearch();

      this.assistants = await assistantsAvailablesToAdd({
        academicYearId: this.academicYearId,
        locationSchoolId: this.schoolId,
        regionId: this.getRegionOfUser,
      });
    },
    async onAddManualAssistant() {
      this.ShowToast(
        '',
        'La información ha sido guardada exitosamente.',
        'success'
      );

      this.toggleAddAssistantSection = false;

      await this.onSearch();

      this.assistants = await assistantsAvailablesToAdd({
        academicYearId: this.academicYearId,
        locationSchoolId: this.schoolId,
        regionId: this.getRegionOfUser,
      });
    },
    async validateIsAnyAssistantCertified(academicYearId, schoolId) {
      const { data: isAny } = await _validateIsAnyAssistantCertified(
        academicYearId,
        schoolId
      );
      this.isAnyAssistantCertified = isAny;
    },
    async certificate() {
      let response = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro(a) que desea certificar la lista de asistentes de servicios?`,
        'Si',
        'No'
      );

      if (!response) return;

      await certifyAssistants(this.academicYearId, this.schoolId);

      this.ShowToast(
        '',
        'La información ha sido guardada exitosamente.',
        'success'
      );

      this.$router.push({ path: '/humanres/irregularstaffrequest' });
    },
    async printAssistantSchoolReport() {
      const { data } = await getAssistantSchoolReport(
        this.academicYearId,
        this.schoolId
      );
      DocumentsService.Download(data);
    },
    async onRegionChangeHandler(val) {
      this.assistants = await assistantsAvailablesToAdd({
        academicYearId: this.academicYearId,
        locationSchoolId: this.schoolId,
        regionId: this.getRegionOfUser,
        isRegionExclusion: val,
      });
    },
  },
  async mounted() {
    await this.fetchResources();

    if (this.schoolId && this.academicYearId) {
      await Promise.all([
        this.validateIsAnyAssistantCertified(
          this.academicYearId,
          this.schoolId
        ),
        this.fetchIsPublicSchool(),
        this.fetchServiceAssistantSchoolInfo(
          this.schoolId,
          this.academicYearId
        ),
      ]);
      await this.onSearch();

      this.assistants = await assistantsAvailablesToAdd({
        academicYearId: this.academicYearId,
        locationSchoolId: this.schoolId,
        regionId: this.getRegionOfUser,
      });
    }
  },
};
</script>
